// [S-270 + S-271 + S-272] Analytics data hooks.
import {
  keepPreviousData,
  useMutation,
  useQuery,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import { useApi, useApiBlob } from "./api";

export type AnalyticsBreakdown = { key: string; count: number };

export type AnalyticsSnapshot = {
  generatedAt: string;
  scope: "tenant" | "platform";
  tenantId: string | null;
  templates: {
    total: number;
    byDealType: AnalyticsBreakdown[];
    byGuild: AnalyticsBreakdown[];
    byTier: AnalyticsBreakdown[];
    byLifecycleState: AnalyticsBreakdown[];
  };
  reviews: {
    total: number;
    avgConfidence: number | null;
    avgDeviations: number | null;
    byDealType: AnalyticsBreakdown[];
    byGuild: AnalyticsBreakdown[];
  };
};

export const ANALYTICS_KEY = ["analytics"] as const;

export function useAnalytics(
  scope: "tenant" | "platform",
  tenantId?: string
): UseQueryResult<AnalyticsSnapshot, Error> {
  const api = useApi("admin");
  const params = new URLSearchParams({ scope });
  if (tenantId !== undefined) params.set("tenantId", tenantId);
  return useQuery({
    queryKey: [...ANALYTICS_KEY, scope, tenantId ?? null] as const,
    // keep prior rows visible while a filter/scope change refetches (no blank flash)
    placeholderData: keepPreviousData,
    queryFn: () =>
      api<AnalyticsSnapshot>(`/admin/analytics?${params.toString()}`),
    staleTime: 60_000,
  });
}

export function useExportAnalytics(): UseMutationResult<
  { filename: string },
  Error,
  { scope: "tenant" | "platform"; tenantId?: string }
> {
  const blobApi = useApiBlob("admin");
  return useMutation({
    mutationFn: async ({ scope, tenantId }) => {
      const params = new URLSearchParams({ scope });
      if (tenantId !== undefined) params.set("tenantId", tenantId);
      const { blob, filename } = await blobApi(
        `/admin/analytics.csv?${params.toString()}`
      );
      const finalName = filename ?? "csai-analytics.csv";
      const objectUrl = URL.createObjectURL(blob);
      try {
        const a = document.createElement("a");
        a.href = objectUrl;
        a.download = finalName;
        document.body.appendChild(a);
        a.click();
        a.remove();
      } finally {
        setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
      }
      return { filename: finalName };
    },
  });
}
